import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { updateSocialMeta } from '../utils/seo';
import { Card, CardContent } from '../components/ui/card';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';

const Contact = () => {
  useEffect(() => {
    document.title = 'Contact Us - Pin High Golf & Lounge | Visit Us in Bay Village, Ohio';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Contact Pin High Golf & Lounge in Bay Village, Ohio. Visit us at 27229 Wolf Road, call (440) 455-1078, or email dennis@pinhighbay.com. Open 7 days a week for simulator bookings and events.');
    }

    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://pinhighbay.com/contact');
    updateSocialMeta({
      title: 'Contact Us - Pin High Golf & Lounge | Visit Us in Bay Village, Ohio',
      description: 'Contact Pin High Golf & Lounge in Bay Village, Ohio. Visit us at 27229 Wolf Road, call (440) 455-1078, or email dennis@pinhighbay.com. Open 7 days a week for simulator bookings and events.',
      path: '/contact',
    });
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-600 to-green-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Get in Touch
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto text-green-50">
            We're here to answer your questions about memberships, lessons, leagues starting November 9th, events, and simulator bookings.
          </p>
        </div>
      </section>

      {/* Contact Information Cards */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-none shadow-lg text-center hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-900">Visit Us</h3>
                <p className="text-gray-600">
                  27229 Wolf Road<br />
                  Bay Village, OH 44140
                </p>
                <a 
                  href="https://maps.google.com/?q=27229+Wolf+Road+Bay+Village+OH" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-green-600 hover:text-green-700 text-sm font-medium mt-3 inline-block"
                >
                  Get Directions →
                </a>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg text-center hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-900">Call Us</h3>
                <p className="text-gray-600 mb-2">
                  <a href="tel:4404551078" className="hover:text-green-600 transition-colors">
                    (440) 455-1078
                  </a>
                </p>
                <p className="text-sm text-gray-500">
                  Available during business hours
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg text-center hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-900">Email Us</h3>
                <p className="text-gray-600 mb-2">
                  <a href="mailto:dennis@pinhighbay.com" className="hover:text-green-600 transition-colors">
                    dennis@pinhighbay.com
                  </a>
                </p>
                <p className="text-sm text-gray-500">
                  We respond within 24 hours
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Form and Hours */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-1 gap-12">
            {/* Hours and Additional Info */}
            <div>
              <Card className="border-none shadow-xl bg-gradient-to-br from-green-50 to-white mb-8">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">Hours of Operation</h3>
                  </div>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                      <span className="font-semibold text-gray-900">Monday - Friday</span>
                      <span className="text-gray-600">9:00 AM - 10:00 PM</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                      <span className="font-semibold text-gray-900">Saturday</span>
                      <span className="text-gray-600">10:00 AM - 11:00 PM</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-900">Sunday</span>
                      <span className="text-gray-600">10:00 AM - 9:00 PM</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-none shadow-xl">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Quick Links</h3>
                  <div className="space-y-3">
                    <a 
                      href="https://app.whoosh.io/patron/club/pin-high-golf-lounge/agenda/pin-high/today" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="block p-4 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-center font-semibold"
                    >
                      Book a Bay Online
                    </a>
                    <Link
                      to="/courses"
                      className="block p-4 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg transition-colors text-center font-semibold"
                    >
                      View Course List
                    </Link>
                  </div>
                </CardContent>
              </Card>

              <div className="mt-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Do I need to book in advance?</h4>
                    <p className="text-gray-600 text-sm">We recommend booking ahead, especially for weekends and evenings. Walk-ins are welcome based on availability.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Is there parking available?</h4>
                    <p className="text-gray-600 text-sm">Yes, we have a large free parking lot adjacent to the facility.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Can I schedule a tour?</h4>
                    <p className="text-gray-600 text-sm">Absolutely! Call us or mention it in the contact form and we'll arrange a tour for you.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
              Find Us in Bay Village
            </h2>
            <p className="text-gray-600">
              Located in the former Bay Lanes space, right in the heart of Bay Village
            </p>
          </div>
          <div className="aspect-video w-full rounded-lg overflow-hidden shadow-2xl">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2986.5!2d-81.920!3d41.485!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8830f5a5a5a5a5a5%3A0x5a5a5a5a5a5a5a5a!2s27229%20Wolf%20Rd%2C%20Bay%20Village%2C%20OH%2044140!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Pin High Golf & Lounge Location"
            ></iframe>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;